import React, { useState } from 'react'
import { 
  useGetOrganizationsQuery, 
  useGetOrganizationStatsQuery,
  useDeleteOrganizationMutation,
  useInviteOrganizationAdminMutation,
} from '../store/api/organizationsApi'
import toast from 'react-hot-toast'
import { formatDateTimeEST } from '../utils/dateUtils'

const Organizations: React.FC = () => {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [showDeleteModal, setShowDeleteModal] = useState<number | null>(null)
  const [invitingId, setInvitingId] = useState<number | null>(null)
  const [inviteModal, setInviteModal] = useState<{ email: string; inviteUrl: string } | null>(null)
  
  const { data: orgsData, isLoading, refetch } = useGetOrganizationsQuery({ 
    page, 
    limit: 20, 
    search: search || undefined 
  })
  const { data: stats } = useGetOrganizationStatsQuery()
  const [deleteOrganization, { isLoading: isDeleting }] = useDeleteOrganizationMutation()
  const [inviteOrganizationAdmin] = useInviteOrganizationAdminMutation()

  const handleDelete = async (id: number) => {
    try {
      await deleteOrganization(id).unwrap()
      toast.success('Organization deleted successfully')
      setShowDeleteModal(null)
      refetch()
    } catch (error) {
      toast.error('Failed to delete organization')
    }
  }

  const handleGetInviteUrl = async (id: number, ownerEmail?: string) => {
    if (!ownerEmail) {
      toast.error('This organization has no owner email')
      return
    }
    try {
      setInvitingId(id)
      const result = await inviteOrganizationAdmin(id).unwrap()
      if (!result?.inviteUrl) {
        toast.error('No invite URL returned')
        return
      }
      setInviteModal({
        email: result.email || ownerEmail,
        inviteUrl: result.inviteUrl,
      })
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to generate admin invite URL')
    } finally {
      setInvitingId(null)
    }
  }

  const copyInviteUrl = async () => {
    if (!inviteModal?.inviteUrl) return
    try {
      await navigator.clipboard.writeText(inviteModal.inviteUrl)
      toast.success('Invite URL copied to clipboard')
    } catch {
      toast.error('Could not copy URL')
    }
  }

  const formatDate = (dateString: string) => formatDateTimeEST(dateString)

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Organizations</h1>
          <p className="text-gray-600">Manage and monitor all organizations</p>
        </div>
        <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
          Add Organization
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Organizations</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.totalOrganizations || 0}</p>
            </div>
            <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 text-sm">🏢</span>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Organizations</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.activeOrganizations || 0}</p>
            </div>
            <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
              <span className="text-green-600 text-sm">📊</span>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Projects/Org</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.avgProjectsPerOrg || 0}</p>
            </div>
            <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center">
              <span className="text-purple-600 text-sm">📁</span>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Members/Org</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.avgMembersPerOrg || 0}</p>
            </div>
            <div className="h-8 w-8 bg-orange-100 rounded-full flex items-center justify-center">
              <span className="text-orange-600 text-sm">👥</span>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search organizations..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button
            onClick={() => refetch()}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Organizations Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Organizations List</h2>
        </div>
        
        <div>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Owner
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Projects
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Members
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Deliverables
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Risk Intel
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  v0+Pretest
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Onboarding
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orgsData?.data?.map((org) => (
                <tr key={org.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{org.name}</div>
                      <div className="text-sm text-gray-500">{org.description || 'No description'}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm capitalize text-gray-900">{org.orgType === 'agency' ? 'Agency' : 'Startup'}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {org.ownerName ? (
                      <span className="text-sm text-gray-900">
                        {org.ownerName}
                        {org.ownerEmail ? ` (${org.ownerEmail})` : ' (email unavailable)'}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">No owner assigned</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{org.projectCount}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{org.memberCount}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{org.requestCount}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${org.riskIntelligenceEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {org.riskIntelligenceEnabled ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${org.v0PretestEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {org.v0PretestEnabled ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">
                      {org.onboardingSource ? (
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 capitalize">
                          {org.onboardingSource}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{formatDate(org.createdAt)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleGetInviteUrl(org.id, org.ownerEmail)}
                        disabled={!org.ownerEmail || invitingId === org.id}
                        className="text-emerald-600 hover:text-emerald-900 disabled:opacity-40"
                        title={org.ownerEmail ? `Get admin invite URL for ${org.ownerEmail}` : 'No owner email'}
                      >
                        {invitingId === org.id ? 'Loading...' : 'Admin invite URL'}
                      </button>
                      <button className="text-blue-600 hover:text-blue-900">
                        Edit
                      </button>
                      <button 
                        onClick={() => setShowDeleteModal(org.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {orgsData?.meta && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((orgsData.meta.page - 1) * orgsData.meta.limit) + 1} to {Math.min(orgsData.meta.page * orgsData.meta.limit, orgsData.meta.total)} of {orgsData.meta.total} organizations
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {page} of {orgsData.meta.totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= orgsData.meta.totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Admin invite URL modal (localhost — copy instead of email) */}
      {inviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Admin invite URL</h2>
              <button
                onClick={() => setInviteModal(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              For <strong>{inviteModal.email}</strong>. Open this on localhost to set their admin panel password (no email sent).
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-2">Invite URL</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={inviteModal.inviteUrl}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
              />
              <button
                type="button"
                onClick={copyInviteUrl}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              >
                Copy
              </button>
            </div>
            <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-3 text-sm text-blue-800">
              This is the same link that would be emailed for admin panel access.
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setInviteModal(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
              <a
                href={inviteModal.inviteUrl}
                target="_blank"
                rel="noreferrer"
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Open in New Tab
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Organization</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this organization? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowDeleteModal(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteModal)}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Organizations 